import { ArrowDropDown, ArrowRight } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  IconButton,
  ListItem,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";

import { monospaceStyle } from "../../theme";

// Least height (px) of a filter row; a long label is cut off on one line, so
// the row stays this high
export const FILTER_ROW_HEIGHT = 30;

// Indent (px) of a row per level beneath the roots
export const FILTER_ROW_INDENT = 14;

// Width (px) the expander takes, and the gap a row without one leaves for it
const EXPANDER_WIDTH = 20;

// What stands before the checkbox: nothing (a flat list), a gap (a row of a
// tree with nothing beneath it), or the triangle that opens and closes it
export type FilterRowExpander = "none" | "gap" | "collapsed" | "expanded";

export interface FilterRowProps {
  label: string;
  // The count as it was given; the row computes nothing
  count: number;
  checked: boolean;
  onChange: (checked: boolean) => void;
  // Levels beneath the roots; 0 for a root and for a flat list
  depth?: number;
  expander?: FilterRowExpander;
  onExpanderClick?: () => void;
  // Drawn between the checkbox and the label, e.g. a country's flag
  icon?: React.ReactNode;
}

// A count as text, with separators between the thousands
export const formatFilterCount = (count: number): string =>
  count.toLocaleString("en-US");

// One row of a filter: an expander where the row has rows beneath it, a
// checkbox, the label, and the count at the far right. Shared by the filter
// tree and the counted list.
const FilterRow: React.FC<FilterRowProps> = ({
  label,
  count,
  checked,
  onChange,
  depth = 0,
  expander = "none",
  onExpanderClick,
  icon,
}) => {
  // Whether the label, as last measured on hover, is cut off; only then is
  // the full label offered in a tooltip
  const [truncated, setTruncated] = useState(false);
  return (
    <ListItem
      disablePadding
      sx={{
        minHeight: FILTER_ROW_HEIGHT,
        pl: `${depth * FILTER_ROW_INDENT}px`,
        alignItems: "center",
      }}
    >
      {expander === "gap" && (
        <Box sx={{ width: EXPANDER_WIDTH, flexShrink: 0 }} />
      )}
      {(expander === "collapsed" || expander === "expanded") && (
        <IconButton
          size="small"
          aria-label={`${expander === "expanded" ? "Collapse" : "Expand"} ${label}`}
          aria-expanded={expander === "expanded"}
          onClick={onExpanderClick}
          sx={{
            width: EXPANDER_WIDTH,
            height: EXPANDER_WIDTH,
            p: 0,
            flexShrink: 0,
            color: "text.secondary",
          }}
        >
          {expander === "expanded" ? (
            <ArrowDropDown fontSize="small" />
          ) : (
            <ArrowRight fontSize="small" />
          )}
        </IconButton>
      )}
      <Box
        component="label"
        sx={{
          display: "flex",
          alignItems: "center",
          flex: 1,
          minWidth: 0,
          cursor: "pointer",
        }}
      >
        <Checkbox
          size="small"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          sx={{ p: 0.5 }}
        />
        {icon && (
          <Box
            component="span"
            aria-hidden
            sx={{ display: "inline-flex", flexShrink: 0, mr: 0.75 }}
          >
            {icon}
          </Box>
        )}
        <Tooltip
          title={truncated ? label : ""}
          describeChild
          placement="right"
          enterDelay={400}
        >
          <Typography
            component="span"
            variant="body2"
            onMouseEnter={(event) => {
              const element = event.currentTarget;
              setTruncated(element.scrollWidth > element.clientWidth);
            }}
            sx={{
              fontSize: "0.8125rem",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: 0,
            }}
          >
            {label}
          </Typography>
        </Tooltip>
      </Box>
      <Typography
        component="span"
        variant="caption"
        color="text.secondary"
        sx={{ ...monospaceStyle, pl: 1, flexShrink: 0 }}
      >
        {formatFilterCount(count)}
      </Typography>
    </ListItem>
  );
};

export default FilterRow;
